import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectAll as selectAllStyles }   from '../../../redux/slices/styles.slice';
import { selectAll as selectAllTeachers } from '../../../redux/slices/teachers.slice';
import { selectAll as selectAllLevels }   from '../../../redux/slices/levels.slice';
import { recitalService } from '../../../services/RecitalService';
import ActCard from './ActCard';
import AddItemButton from './AddItemButton';
import RightPanel from './RightPanel';
import AssignBoard from './AssignBoard';
import RoutineDialog from './RoutineDialog';
import ColorMenu from './ColorMenu';
import ClassLibrary from './ClassLibrary';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiPlus, FiDownload } from 'react-icons/fi';

const DAY_ABBR = { monday:'Mon', tuesday:'Tue', wednesday:'Wed', thursday:'Thu', friday:'Fri', saturday:'Sat', sunday:'Sun' };

// Build a {name → color} map from redux entities
function buildColorMap(styles, teachers, levels) {
    const map = {};
    styles.forEach(s => { if (s.styleName && s.styleColor) map[s.styleName] = s.styleColor; });
    teachers.forEach(t => { if (t.teacherName && t.teacherColor) map[t.teacherName] = t.teacherColor; });
    levels.forEach(l => { if (l.levelName && l.color) map[l.levelName] = l.color; });
    return map;
}

// Class info for an act: prefer the recital's own copy, fall back to the
// legacy schedule sticker for acts created before copies existed.
export function actClass(act) {
    return act?.recitalClass || act?.scheduleSticker || null;
}

// Quick changes are worked out by the server (GET /recitals/:id returns them per
// show, matched by dancer NAME because recital classes are independent copies and
// the same dancer has a different roster id in each). The screen used to do this
// itself keyed by id, which could never match across copies, so the warning never
// fired on a copied recital. Andrew, staging, 2026-10-09.
// Family conflict detection: same last name across different shows = a family
// that has to come more than once. Each family now carries WHO is in WHICH show
// and in which class, so the panel can show why it was flagged. Andrew,
// 2026-10-09: "more clarity on what is in what show".
function detectFamilyConflicts(shows) {
    const families = {}; // lastName -> { shows: Set, members: Map(name -> Set of "Show · Class") }
    shows.forEach(show => {
        const showName = show.name || `Show ${show.id}`;
        (show.acts || []).forEach(act => {
            if (act.type !== 'class') return;
            const cls = actClass(act);
            const className = cls?.className || act.label || 'Untitled';
            (cls?.students || []).forEach(s => {
                const clean = String(s.name || '').trim().replace(/\s+/g, ' ');
                if (!clean) return;
                const parts = clean.split(' ');
                const lastName = parts.length > 1 ? parts[parts.length - 1] : parts[0];
                const fam = families[lastName] || (families[lastName] = { shows: new Set(), members: new Map() });
                fam.shows.add(showName);
                const key = clean.toLowerCase();
                if (!fam.members.has(key)) fam.members.set(key, { name: clean, shows: new Set(), where: new Set(), places: [] });
                fam.members.get(key).shows.add(showName);
                fam.members.get(key).where.add(`${showName} · ${className}`);
                fam.members.get(key).places.push({ showIdx: shows.indexOf(show), show: showName, className });
            });
        });
    });
    return Object.entries(families)
        .filter(([, fam]) => fam.shows.size > 1)
        .map(([lastName, fam]) => ({
            lastName,
            shows: [...fam.shows].sort(),
            members: [...fam.members.values()]
                .map(m => ({ name: m.name, shows: [...m.shows].sort(), where: [...m.where].sort(),
                    places: [...m.places].sort((a, b) => a.showIdx - b.showIdx || a.className.localeCompare(b.className)) }))
                .sort((a, b) => a.name.localeCompare(b.name)),
        }))
        .sort((a, b) => a.lastName.localeCompare(b.lastName));
}

// "All good": a family Andrew does not want flagged (comp and company families are
// at every show anyway, and a shared surname is not always a family). Kept per
// recital in this browser for now; a server-side record is the proper home and
// is owed. Andrew, 2026-10-09: "definitely an ignore function".
const ignoredFamiliesKey = (recitalId) => `recital_${recitalId}_families_all_good`;
function readIgnoredFamilies(recitalId) {
    try { return JSON.parse(localStorage.getItem(ignoredFamiliesKey(recitalId)) || '[]'); } catch { return []; }
}
function writeIgnoredFamilies(recitalId, list) {
    try { localStorage.setItem(ignoredFamiliesKey(recitalId), JSON.stringify(list)); } catch { /* storage unavailable: the list just does not persist */ }
}

export default function RecitalEditor({ recital, onBack, onUpdate }) {
    const allStyles   = useSelector(selectAllStyles);
    const allTeachers = useSelector(selectAllTeachers);
    const allLevels   = useSelector(selectAllLevels);
    const colorMap         = buildColorMap(allStyles, allTeachers, allLevels);
    // Reverse map: HSL color string → level name (levels stored as colors on ScheduleSticker)
    const colorToLevelName = Object.fromEntries(
        allLevels.filter(l => l.color && l.levelName).map(l => [l.color, l.levelName])
    );

    const [currentShowIdx, setCurrentShowIdx] = useState(0);
    const [shows, setShows]                   = useState(recital.shows || []);
    // Saved with the recital (mockup v6): what the colour means and how it shows.
    const [colorBy, setColorBy]               = useState(recital.colorBy || 'style');
    const [colorLook, setColorLook]           = useState(recital.colorLook || 'stripe');
    // The quick-change gap is the studio's saved setting, not a screen default:
    // it comes off the recital record and goes back to it when changed.
    const [bufferActs, setBufferActs]         = useState(recital.quickChangeGap ?? 2);
    const [showExportMenu, setShowExportMenu] = useState(false);
    const [addingShow, setAddingShow]         = useState(false);
    const [customLabel, setCustomLabel]       = useState('');
    const [showCustomPrompt, setShowCustomPrompt] = useState(null);
    // { routine: null } adds a new routine to the current show; { routine: cls } edits one.
    const [routineDialog, setRoutineDialog]   = useState(null);
    const [ignoredFamilies, setIgnoredFamilies] = useState(() => readIgnoredFamilies(recital.id));
    const [viewMode, setViewMode] = useState('board'); // 'board' (assign) | 'show' (running order)
    const [renamingShowId, setRenamingShowId]   = useState(null);
    const [renameValue, setRenameValue]         = useState('');
    const [confirmDeleteShow, setConfirmDeleteShow] = useState(null); // show object to delete

    const dragItem    = useRef(null);
    const dragOverItem= useRef(null);
    const exportRef   = useRef();

    // Close export menu on outside click
    useEffect(() => {
        const h = (e) => { if (exportRef.current && !exportRef.current.contains(e.target)) setShowExportMenu(false); };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, []);

    const currentShow = shows[currentShowIdx];
    const acts = [...(currentShow?.acts || [])].sort((a,b) => a.position - b.position);

    const refreshRecital = useCallback(async () => {
        const res = await recitalService.getRecital(recital.id);
        const updated = res.data.recital;
        setShows(updated.shows || []);
        onUpdate(updated);
    }, [recital.id, onUpdate]);

    // ---- Shows ----

    const addShow = async () => {
        setAddingShow(true);
        try {
            const name = `Show ${shows.length + 1}`;
            await recitalService.addShow(recital.id, { name, position: shows.length });
            await refreshRecital();
            setCurrentShowIdx(shows.length);
        } finally { setAddingShow(false); }
    };

    const startRename = (show) => {
        setRenamingShowId(show.id);
        setRenameValue(show.name);
    };

    const commitRename = async (showId) => {
        const val = renameValue.trim();
        if (val) {
            try {
                await recitalService.updateShow(showId, { name: val });
                await refreshRecital();
            } catch(e) { console.error(e); toast.error('Could not rename the show. Please try again.'); }
        }
        setRenamingShowId(null);
        setRenameValue('');
    };

    const handleDeleteShow = async () => {
        if (!confirmDeleteShow) return;
        try {
            await recitalService.deleteShow(confirmDeleteShow.id);
            setConfirmDeleteShow(null);
            setCurrentShowIdx(0);
            await refreshRecital();
        } catch(e) { console.error(e); toast.error('Could not delete the show. Please try again.'); }
    };

    // ---- Acts / drag-and-drop ----

    const onDragStart  = (id) => { dragItem.current = id; };
    const onDragOver   = (id) => { dragOverItem.current = id; };
    const onDragEnd    = async () => {
        if (dragItem.current === dragOverItem.current || !currentShow) return;
        const ordered = [...acts];
        const fromIdx = ordered.findIndex(a => a.id === dragItem.current);
        const toIdx   = ordered.findIndex(a => a.id === dragOverItem.current);
        if (fromIdx === -1 || toIdx === -1) return;
        const [moved] = ordered.splice(fromIdx, 1);
        ordered.splice(toIdx, 0, moved);
        // Optimistic update
        const updatedShows = shows.map((s, i) =>
            i === currentShowIdx ? { ...s, acts: ordered.map((a,pos) => ({ ...a, position: pos })) } : s
        );
        setShows(updatedShows);
        dragItem.current = null;
        dragOverItem.current = null;
        try {
            await recitalService.reorderActs(currentShow.id, ordered.map(a => a.id));
            // The quick-change flags come from the server, so re-read once the new
            // order is saved; the optimistic order above stays put meanwhile.
            await refreshRecital();
        } catch (e) {
            console.error('Reorder failed', e);
            toast.error('Could not save the new order. It has been reset.');
            await refreshRecital();
        }
    };

    // One place for "put this class in that show / take it out", used by the
    // library's show pips in both views.
    const toggleClassInShow = async (cls, showIdx) => {
        const show = shows[showIdx];
        if (!show) return;
        const existing = (show.acts || []).find(a => a.recitalClassId === cls.id);
        try {
            if (existing) await recitalService.deleteAct(existing.id);
            else await recitalService.addAct(show.id, { recitalClassId: cls.id, type: 'class', position: (show.acts || []).length });
            await refreshRecital();
        } catch (e) {
            console.error(e);
            toast.error(existing ? 'Could not remove that class from the show. Please try again.' : 'Could not add that class to the show. Please try again.');
        }
    };

    const deleteAct = async (actId) => {
        if (!currentShow) return;
        try {
            await recitalService.deleteAct(actId);
            await refreshRecital();
        } catch (e) { console.error(e); toast.error('Could not remove that act. Please try again.'); }
    };

    const changeActDuration = async (actId, durationSecs) => {
        try {
            await recitalService.updateAct(actId, { durationSecs });
            await refreshRecital();
        } catch (e) { console.error(e); toast.error('Could not save the new duration. Please try again.'); }
    };

    const addItem = async (type, targetShow = currentShow) => {
        if (!targetShow) return;
        const targetActs = targetShow.acts || [];
        if (type === 'unlisted') {
            // A routine: a number that is not on the schedule but has dancers.
            setRoutineDialog({ routine: null, show: targetShow, position: targetActs.length });
            return;
        }
        if (type === 'custom') {
            setShowCustomPrompt({ type, show: targetShow, position: targetActs.length });
            return;
        }
        try {
            await recitalService.addAct(targetShow.id, {
                type,
                label: type === 'intermission' ? 'Intermission' : '',
                position: targetActs.length,
                scheduleStickerId: null,
            });
            await refreshRecital();
        } catch (e) { console.error(e); toast.error('Could not add that item. Please try again.'); }
    };

    const confirmCustom = async () => {
        if (!showCustomPrompt?.show) return;
        try {
            await recitalService.addAct(showCustomPrompt.show.id, {
                type: showCustomPrompt.type,
                label: customLabel || 'Custom moment',
                position: showCustomPrompt.position,
                scheduleStickerId: null,
            });
            setCustomLabel('');
            setShowCustomPrompt(null);
            await refreshRecital();
        } catch (e) { console.error(e); toast.error('Could not add that item. Please try again.'); }
    };

    // ---- Export ----

    const triggerDownload = (blob, filename) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
    };

    const exportPdf = async () => {
        if (!currentShow) return;
        setShowExportMenu(false);
        try {
            const res = await recitalService.exportPdf(currentShow.id, colorBy, colorMap, colorToLevelName);
            triggerDownload(res.data, 'recital-order.pdf');
        } catch (e) { console.error(e); toast.error('The PDF export failed. Please try again.'); }
    };

    const exportXls = async () => {
        if (!currentShow) return;
        setShowExportMenu(false);
        try {
            const res = await recitalService.exportXls(currentShow.id, colorBy, colorMap, colorToLevelName);
            triggerDownload(res.data, 'recital-order.xlsx');
        } catch (e) { console.error(e); toast.error('The spreadsheet export failed. Please try again.'); }
    };

    // ---- Quick changes ----

    // Persist the gap so it survives a reload. The flags update straight away
    // from local state; if the save fails the number is put back rather than
    // left showing a value the recital does not actually hold.
    // Colour settings save the same way: apply at once, put back if the save fails.
    const saveColorSettings = async ({ colorBy: nextBy, colorLook: nextLook }) => {
        const prevBy = colorBy, prevLook = colorLook;
        if (nextBy) setColorBy(nextBy);
        if (nextLook) setColorLook(nextLook);
        try {
            await recitalService.updateRecital(recital.id, {
                ...(nextBy ? { colorBy: nextBy } : {}),
                ...(nextLook ? { colorLook: nextLook } : {}),
            });
        } catch (err) {
            setColorBy(prevBy); setColorLook(prevLook);
            toast.error('Could not save the colour setting. Please try again.');
        }
    };

    const saveBufferActs = async (value) => {
        const previous = bufferActs;
        setBufferActs(value);
        try {
            await recitalService.updateRecital(recital.id, { quickChangeGap: value });
            await refreshRecital();
        } catch (err) {
            setBufferActs(previous);
        }
    };

    const quickChanges     = currentShow?.quickChanges || [];
    const allFamilies      = detectFamilyConflicts(shows);
    const familyConflicts  = allFamilies.filter(f => !ignoredFamilies.includes(f.lastName));
    const quietedFamilies  = allFamilies.filter(f => ignoredFamilies.includes(f.lastName));
    const markFamilyAllGood = (lastName) => {
        const next = [...new Set([...ignoredFamilies, lastName])];
        setIgnoredFamilies(next); writeIgnoredFamilies(recital.id, next);
    };
    const undoFamilyAllGood = (lastName) => {
        const next = ignoredFamilies.filter(n => n !== lastName);
        setIgnoredFamilies(next); writeIgnoredFamilies(recital.id, next);
    };
    const quickChangeIds   = new Set(quickChanges.flatMap(qc => [qc.actIdA, qc.actIdB]).filter(Boolean));

    // ---- Render ----

    const toolbarS = {
        wrap:   { display:'flex', alignItems:'center', gap:8, padding:'0 20px', height:52,
                  background:'#fff', borderBottom:'1px solid #e0e0ec', flexShrink:0 },
        back:   { display:'flex', alignItems:'center', gap:6, background:'none', border:'none', cursor:'pointer',
                  color:'#6b6b8a', fontSize:13, padding:'6px 8px', borderRadius:6, marginRight:4 },
        title:  { fontSize:15, fontWeight:700, color:'#3f3f55' },
        btn:    (active) => ({ display:'flex', alignItems:'center', gap:6, background: active ? '#e6f7fd' : 'none',
                  border: `1px solid ${active ? '#29b8e5' : '#e0e0ec'}`, borderRadius:7, padding:'6px 12px',
                  fontSize:12, cursor:'pointer', color: active ? '#29b8e5' : '#3f3f55', fontWeight: active ? 600 : 400 }),
    };

    let actNumber = 0;

    return (
        <div style={{ display:'flex', flexDirection:'column', height:'100vh', background:'#f2f3f9' }}>
            {/* Toolbar */}
            <div style={toolbarS.wrap}>
                <button style={toolbarS.back} onClick={onBack}><FiArrowLeft size={14}/> Recitals</button>
                <span style={toolbarS.title}>{recital.name}</span>

                {/* View toggle sits by the title; Export goes last as the final step.
                    Andrew, 2026-10-12: the toggle was "too far out to the right". */}
                <div style={{ display:'flex', border:'1px solid #e0e0ec', borderRadius:8, overflow:'hidden', marginLeft:14 }}>
                    {[{id:'board',label:'Assign board'},{id:'show',label:'Running order'}].map(v => (
                        <button key={v.id} onClick={()=>setViewMode(v.id)}
                            style={{ padding:'6px 14px', fontSize:12, background: viewMode===v.id?'#f2f3f9':'none',
                                border:'none', borderRight:'1px solid #e0e0ec', cursor:'pointer',
                                color: viewMode===v.id?'#3f3f55':'#6b6b8a', fontWeight: viewMode===v.id?600:400 }}>
                            {v.label}
                        </button>
                    ))}
                </div>

                <div style={{ marginLeft:'auto' }}/>

                {/* Export */}
                <div ref={exportRef} style={{ position:'relative' }}>
                    <button style={toolbarS.btn(false)} onClick={() => setShowExportMenu(o=>!o)}>
                        <FiDownload size={13}/> Export
                    </button>
                    {showExportMenu && (
                        <div style={{ position:'absolute', top:'calc(100% + 6px)', right:0, background:'#fff',
                            border:'1px solid #e0e0ec', borderRadius:10, boxShadow:'0 4px 20px rgba(0,0,0,0.12)',
                            overflow:'hidden', zIndex:50, minWidth:160 }}>
                            {[{label:'Export as PDF', fn:exportPdf},{label:'Export as XLS', fn:exportXls}].map(o=>(
                                <div key={o.label} style={{ padding:'11px 16px', fontSize:13, cursor:'pointer', color:'#3f3f55' }}
                                    onMouseEnter={e=>e.currentTarget.style.background='#f2f3f9'}
                                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}
                                    onClick={o.fn}>{o.label}</div>
                            ))}
                        </div>
                    )}
                </div>

            </div>

            {/* One settings row for both views: colour and the quick change gap.
                Replaces the Preferences block in the right panel. Mockup v6. */}
            <div style={{ display:'flex', alignItems:'center', gap:10, padding:'0 20px', height:44,
                background:'#fff', borderBottom:'1px solid #e0e0ec', flexShrink:0 }}>
                <ColorMenu colorBy={colorBy} colorLook={colorLook} onChange={saveColorSettings} />
                <div style={{ width:1, height:22, background:'#e0e0ec', margin:'0 4px' }}/>
                <span style={{ fontSize:11, fontWeight:600, color:'#7a7a99', textTransform:'uppercase', letterSpacing:0.4 }}>Quick change gap</span>
                <select value={bufferActs} onChange={e => saveBufferActs(Number(e.target.value))}
                    aria-label="Quick change gap"
                    style={{ fontSize:12, border:'1px solid #d9d9e8', borderRadius:7, padding:'5px 8px', background:'#fff',
                        color:'#3f3f55', fontFamily:'inherit', cursor:'pointer' }}>
                    {[1,2,3,4,5].map(n => <option key={n} value={n}>{n} routine{n===1?'':'s'}</option>)}
                </select>
                {/* Counts at a glance, so the owner sees trouble without scrolling the panel. */}
                <div style={{ marginLeft:'auto', display:'flex', gap:6 }}>
                    {(() => {
                        const qcCount = viewMode === 'board'
                            ? shows.reduce((n, s) => n + (s.quickChanges || []).length, 0)
                            : quickChanges.length;
                        const pill = (warn) => ({ display:'flex', alignItems:'center', gap:6, fontSize:12, fontWeight:500, borderRadius:7, padding:'5px 11px',
                            color: warn ? '#d4820a' : '#2ab568', background: warn ? '#fffbf0' : '#f0faf4', border:`1px solid ${warn ? '#f0c87a' : '#a0dbb0'}` });
                        return (
                            <>
                                <span style={pill(qcCount > 0)}>{qcCount > 0 ? '⚠' : '✓'} {qcCount} quick change{qcCount === 1 ? '' : 's'}{viewMode === 'board' && shows.length > 1 ? ' (all shows)' : ''}</span>
                                <span style={pill(familyConflicts.length > 0)}>{familyConflicts.length > 0 ? '⚠' : '✓'} {familyConflicts.length} {familyConflicts.length === 1 ? 'family' : 'families'}</span>
                            </>
                        );
                    })()}
                </div>
            </div>

            {/* Show tabs. Not on the Assign Board: every show is a column there and the
                board has its own Add show. Andrew, 2026-10-12: "the individual show
                buttons shouldn't exist" on the board. */}
            {viewMode !== 'board' && (
            <div style={{ display:'flex', alignItems:'center', background:'#fff', borderBottom:'1px solid #e0e0ec',
                padding:'0 20px', gap:4, flexShrink:0, overflowX:'auto' }}>
                {shows.map((show, i) => (
                    <div key={show.id} style={{ display:'flex', alignItems:'center' }}>
                        {renamingShowId === show.id ? (
                            <input
                                autoFocus
                                value={renameValue}
                                onChange={e => setRenameValue(e.target.value)}
                                onBlur={() => commitRename(show.id)}
                                onKeyDown={e => { if (e.key === 'Enter') commitRename(show.id); if (e.key === 'Escape') setRenamingShowId(null); }}
                                style={{ padding:'6px 10px', fontSize:13, fontWeight:600, color:'#29b8e5',
                                    border:'1px solid #29b8e5', borderRadius:6, outline:'none',
                                    width: Math.max(80, renameValue.length * 8 + 20) + 'px' }}
                            />
                        ) : (
                            <button
                                onClick={() => {
                                    setCurrentShowIdx(i);
                                    // On the Assign Board every show is already on screen, so
                                    // a show tab opens that show's running order instead of
                                    // changing a selection nothing shows. Andrew, 2026-09-18:
                                    // "the show buttons don't do anything."
                                    if (viewMode === 'board') setViewMode('show');
                                }}
                                onDoubleClick={() => startRename(show)}
                                title="Double-click to rename"
                                style={{ padding:'10px 14px 10px 18px', fontSize:13, fontWeight: i===currentShowIdx ? 600 : 400,
                                    color: i===currentShowIdx ? '#29b8e5' : '#3f3f55', background:'none', border:'none',
                                    cursor:'pointer', borderBottom: i===currentShowIdx ? '2px solid #29b8e5' : '2px solid transparent',
                                    whiteSpace:'nowrap', display:'flex', alignItems:'center', gap:6 }}>
                                {show.name}
                                {i === currentShowIdx && shows.length > 1 && (
                                    <span
                                        onClick={e => { e.stopPropagation(); setConfirmDeleteShow(show); }}
                                        title="Delete show"
                                        style={{ fontSize:14, lineHeight:1, color:'#c0c0d0', cursor:'pointer',
                                            display:'inline-flex', alignItems:'center',
                                            borderRadius:4, padding:'0 2px' }}
                                        onMouseEnter={e => e.currentTarget.style.color='#e07b2a'}
                                        onMouseLeave={e => e.currentTarget.style.color='#c0c0d0'}>
                                        ×
                                    </span>
                                )}
                            </button>
                        )}
                    </div>
                ))}
                <button onClick={addShow} disabled={addingShow}
                    style={{ display:'flex', alignItems:'center', gap:4, padding:'8px 12px', fontSize:12,
                        color:'#6b6b8a', background:'none', border:'none', cursor:'pointer' }}>
                    <FiPlus size={13}/> Add Show
                </button>
            </div>
            )}

            {/* Main area */}
            <div style={{ display:'flex', flex:1, overflow:'hidden' }}>
                {/* Board view */}
                {viewMode === 'board' && (
                    <AssignBoard
                        onAddItem={(type, show) => addItem(type, show)}
                        onToggleInShow={toggleClassInShow}
                        colorBy={colorBy}
                        colorLook={colorLook}
                        recital={recital}
                        shows={shows}
                        colorMap={colorMap}
                        colorToLevelName={colorToLevelName}
                        onRefresh={refreshRecital}
                        onOpenShow={(idx) => { setCurrentShowIdx(idx); setViewMode('show'); }}
                        onAddShow={addShow}
                    />
                )}

                {/* Show view — running order, with the same library on the left */}
                {viewMode === 'show' && (
                    <ClassLibrary
                        recital={recital} shows={shows}
                        colorBy={colorBy} colorLook={colorLook} colorMap={colorMap} colorToLevelName={colorToLevelName}
                        onToggleInShow={toggleClassInShow} onRefresh={refreshRecital}
                    />
                )}
                {viewMode === 'show' && (
                    <div style={{ flex:1, overflowY:'auto', padding:'20px' }}>
                        {!currentShow ? (
                            <p style={{ color:'#6b6b8a', fontSize:13 }}>No show selected.</p>
                        ) : acts.length === 0 ? (
                            <div style={{ textAlign:'center', padding:'60px 0', color:'#6b6b8a' }}>
                                <div style={{ fontSize:16, fontWeight:600, color:'#3f3f55', marginBottom:8 }}>No acts yet</div>
                                <p style={{ fontSize:13 }}>Click a show number in the class library on the left to add classes.</p>
                            </div>
                        ) : (
                            acts.map(act => {
                                if (act.type === 'class') actNumber++;
                                return (
                                    <ActCard
                                        key={act.id}
                                        act={act}
                                        index={acts.indexOf(act)}
                                        actNumber={act.type === 'class' ? actNumber : null}
                                        colorBy={colorBy}
                                        colorLook={colorLook}
                                        colorMap={colorMap}
                                        colorToLevelName={colorToLevelName}
                                        quickChangeIds={quickChangeIds}
                                        onDelete={deleteAct}
                                        onDurationChange={changeActDuration}
                                        onEditRoutine={(cls) => setRoutineDialog({ routine: cls })}
                                        onDragStart={onDragStart}
                                        onDragOver={onDragOver}
                                        onDrop={()=>{}}
                                        onDragEnd={onDragEnd}
                                        isDragOver={false}
                                    />
                                );
                            })
                        )}
                        {currentShow && <AddItemButton onAdd={addItem}/>}
                    </div>
                )}

                {/* Right panel — always visible */}
                <RightPanel
                    acts={acts}
                    quickChanges={quickChanges}
                    groups={viewMode === 'board' ? shows.map(s => ({ id: s.id, name: s.name || 'Show', acts: s.acts || [], quickChanges: s.quickChanges || [] })) : null}
                    familyConflicts={familyConflicts}
                    quietedFamilies={quietedFamilies}
                    onFamilyAllGood={markFamilyAllGood}
                    onFamilyUndo={undoFamilyAllGood}
                />
            </div>

            {/* Delete show confirmation */}
            {confirmDeleteShow && (
                <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:200,
                    display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <div style={{ background:'#fff', borderRadius:14, padding:28, width:380,
                        boxShadow:'0 8px 40px rgba(0,0,0,0.18)' }}>
                        <div style={{ fontSize:16, fontWeight:700, color:'#3f3f55', marginBottom:8 }}>
                            Delete "{confirmDeleteShow.name}"?
                        </div>
                        <p style={{ fontSize:13, color:'#6b6b8a', marginBottom:24, lineHeight:1.5 }}>
                            This will permanently delete the show and all of its acts. The classes themselves
                            won't be affected. They'll still be on your other shows and schedule.
                        </p>
                        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
                            <button onClick={() => setConfirmDeleteShow(null)}
                                style={{ padding:'9px 18px', borderRadius:8, border:'1px solid #e0e0ec',
                                    background:'#fff', fontSize:13, cursor:'pointer', color:'#3f3f55' }}>
                                Cancel
                            </button>
                            <button onClick={handleDeleteShow}
                                style={{ padding:'9px 18px', borderRadius:8, border:'none',
                                    background:'#e05252', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer' }}>
                                Delete show
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* New or edit routine: name plus roster */}
            {routineDialog && (
                <RoutineDialog
                    recital={recital}
                    show={routineDialog.show || currentShow}
                    position={routineDialog.position ?? acts.length}
                    routine={routineDialog.routine}
                    onClose={() => setRoutineDialog(null)}
                    onSaved={refreshRecital}
                />
            )}

            {/* Custom label prompt */}
            {showCustomPrompt && (
                <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <div style={{ background:'#fff', borderRadius:12, padding:28, width:360 }}>
                        <div style={{ fontSize:15, fontWeight:700, color:'#3f3f55', marginBottom:14 }}>
                            Custom moment label
                        </div>
                        <input autoFocus value={customLabel} onChange={e=>setCustomLabel(e.target.value)}
                            onKeyDown={e=>e.key==='Enter'&&confirmCustom()}
                            placeholder="e.g. Welcome remarks"
                            style={{ width:'100%', border:'1px solid #e0e0ec', borderRadius:8, padding:'10px 12px',
                                fontSize:14, outline:'none', boxSizing:'border-box', marginBottom:16 }}/>
                        <div style={{ display:'flex', justifyContent:'flex-end', gap:10 }}>
                            <button onClick={()=>{setShowCustomPrompt(null);setCustomLabel('');}}
                                style={{ background:'none', border:'1px solid #e0e0ec', borderRadius:8, padding:'8px 18px', cursor:'pointer', fontSize:13 }}>
                                Cancel
                            </button>
                            <button onClick={confirmCustom}
                                style={{ background:'#29b8e5', border:'none', borderRadius:8, padding:'8px 18px', color:'#fff', fontWeight:600, fontSize:13, cursor:'pointer' }}>
                                Add
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
